import React from 'react';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import TradeOrderVM from './TradeOrderVM';
import { observer } from 'mobx-react';
import Loading from '../../../App/Components/Loading/Loading';
import { TextConstants } from '../../../Common/TextConstants';
import TradeOrderLookup from '../Models/Lookups/TradeOrderLookup';
import UpdateTradeOrderCommand from '../Models/Commands/UpdateTradeOrderCommand';
import { EnumHelper } from '@singularsystems/neo-core';
import { TradeOrderStatus } from '../Models/Enums/TradeOrderStatus';

@observer
export default class TradeOrderView extends Views.ViewBase<TradeOrderVM> {

    private renderFileDropzone(type: 'confirm' | 'reject') {
        const hasFile = this.viewModel.uploadedFile && this.viewModel.uploadFileType === type;
        const fileLabel = type === 'confirm' ? 'confirmation' : 'rejection';

        return (
            <div style={{ marginBottom: 16 }}>
                <label style={{ fontWeight: 600, marginBottom: 8, display: 'block', color: '#333' }}>
                    {TextConstants.Titles.AttachmentOptional}
                </label>
                <Neo.FileContext
                    fileManager={this.viewModel.tradeOrderAttachmentsManager}
                    onFilesSelected={(fileList: File[] | FileList) => {
                        this.viewModel.handleFileSelection(fileList);
                        const file = Array.from(fileList)[0] ?? null;
                    }}
                    allowedExtensions={[".pdf", ".jpg", ".jpeg", ".png", ".doc", ".docx"]}
                >
                    <Neo.FileDropArea className="text-center" fileManager={this.viewModel.tradeOrderAttachmentsManager}>
                        <div style={{ fontSize: 32, color: '#bdbdbd', marginBottom: 8 }}>
                            <i className="fa fa-upload" />
                        </div>
                        <div className="mt-3 mb-3"> Drag and drop to upload a {fileLabel} file or</div>
                        <Neo.FileUploadButton variant="primary" isOutline>{TextConstants.Buttons.BrowseFilesHere}</Neo.FileUploadButton>
                        <div style={{ color: '#999', fontSize: 12, marginTop: 12 }}>
                            Max 10MB • PDF, Images, Word
                        </div>
                    </Neo.FileDropArea>
                </Neo.FileContext>
                <Neo.FileUploadProgress fileManager={this.viewModel.tradeOrderAttachmentsManager} />
                {( (this.viewModel.uploadedFiles && this.viewModel.uploadedFiles.length > 0)) && (
                    <div className="my-2 d-flex align-items-center">
                        <span className="me-2">{this.viewModel.uploadedFiles[0].name} ({(this.viewModel.uploadedFiles[0].size / 1024).toFixed(1)} KB)</span>
                        <Neo.Button size="sm" variant="danger" isOutline icon="fa fa-times" onClick={() => { this.viewModel.uploadedFiles = undefined; }} />
                    </div>
                )}
            </div>
        );
    }

    constructor(props: unknown) {
        super(TextConstants.Titles.TradeOrders, TradeOrderVM, props);
    }

    public async componentDidMount() {
        // Check permission before loading the page
        if (!this.viewModel.canViewAll) {
            alert(TextConstants.PlaceHolder.ViewTradeOrdersPermissionDenied);
            this.viewModel.navigateToHome()
            return;
        }
    }

    public render() {
        // Don't render anything if user doesn't have permission
        if (!this.viewModel.canViewAll) {
            return (
                <Neo.Card title={TextConstants.Titles.AccessDenied} icon="fa fa-exclamation-triangle">
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <p>You do not have permission to view trade orders.</p>
                        <Neo.Button
                            variant="primary"
                            icon="fa fa-home"
                            onClick={() => this.viewModel.navigateToHome()}
                        >
                            {TextConstants.Buttons.ReturnToHome}
                        </Neo.Button>
                    </div>
                </Neo.Card>
            );
        }

        const disableInlineActions = this.viewModel.selectedTradeOrderIDs.length > 0;

        return (
            <>
                {this.viewModel.taskRunner.isBusy && <Loading />}
                {/* Search Criteria Card */}
                <Neo.Card title={TextConstants.Titles.SearchCriteria} icon="fa fa-search">
                    <Neo.Form model={this.viewModel.tradeOrderSearchCriteria} onSubmit={() => this.viewModel.pageManager.refreshData()}>
                        <Neo.GridLayout md={4}>
                            <Neo.FormGroupInline
                                bind={this.viewModel.tradeOrderSearchCriteria.meta.staticServiceProviderFileGroupingID}
                                select={{
                                    displayMember: "groupingDescription",
                                    items: this.viewModel.allServiceProviders,
                                    allowNulls: true,
                                    nullText: "All Brokers",
                                    onItemSelected: () => this.viewModel.pageManager.refreshData()
                                }}
                                placeholder="Service Provider"
                            />
                            <Neo.FormGroupInline bind={this.viewModel.tradeOrderSearchCriteria.meta.tradeDescription} />
                            <Neo.FormGroupInline bind={this.viewModel.tradeOrderSearchCriteria.meta.referenceNumber} />
                            <Neo.FormGroupInline
                                bind={this.viewModel.tradeOrderSearchCriteria.meta.tradeOrderStatusId}
                                select={{
                                    items: EnumHelper.asList(TradeOrderStatus),
                                    displayMember: "name",
                                    valueMember: "id",
                                    allowNulls: true,
                                    nullText: "All Statuses"
                                }}
                                onChange={() => this.viewModel.pageManager.refreshData()}
                            />
                            <Neo.FormGroupInline bind={this.viewModel.tradeOrderSearchCriteria.meta.fromDate} />
                            <Neo.FormGroupInline bind={this.viewModel.tradeOrderSearchCriteria.meta.toDate} />
                        </Neo.GridLayout>
                        <Neo.Button isSubmit variant="primary" icon="fa fa-search">{TextConstants.Buttons.Search}</Neo.Button>
                        <Neo.Button variant="secondary"
                            icon="fa fa-times"
                            style={{ marginLeft: '10px' }}
                            onClick={() => this.viewModel.clearSearch()}>
                            {TextConstants.Buttons.ClearSearch}
                        </Neo.Button>
                    </Neo.Form>
                </Neo.Card>

                {/* Trade Orders Grid Card */}
                <Neo.Card title={TextConstants.Titles.TradeOrders} icon="fa fa-list">
                        <Neo.Button
                            variant="success"
                            icon="fa fa-paper-plane"
                            style={{ marginRight: '10px' }}
                            onClick={this.viewModel.handleBulkRelease}
                            disabled={this.viewModel.selectedTradeOrderIDs.length === 0}
                        >
                            {TextConstants.Buttons.GlobalRelease}
                        </Neo.Button>
                        <Neo.Button
                            variant="primary"
                            icon="fa fa-download"
                            style={{marginRight: '10px' }}
                            onClick={this.viewModel.handleBulkDownload}
                            tooltip="Download all filtered results as a .csv file"
                        >
                            {TextConstants.Buttons.GlobalDownload}
                        </Neo.Button>
                    <Neo.Pager
                        pageManager={this.viewModel.pageManager}
                        pageControlProps={{ pageSizeLabel: "Show:" }}
                        style={{ paddingTop: "20px" }}
                    >
                        <div className="row overflow-auto">
                            <NeoGrid.Grid<TradeOrderLookup>
                                items={this.viewModel.getFilteredItems()}
                                className="table-striped"
                                initialSort={'cycleDate'}
                                initialSortAscending={false}
                                allowSort
                            >
                                {(item: TradeOrderLookup) => {
                                    const isPending = item.tradeOrderStatusId === TradeOrderStatus.Pending;
                                    const isReleased = item.tradeOrderStatusId === TradeOrderStatus.Released;
                                    const isRejected = item.tradeOrderStatusId === TradeOrderStatus.Rejected;
                                    const canReject = [TradeOrderStatus.Pending, TradeOrderStatus.Released].includes(item.tradeOrderStatusId);

                                    return (
                                        <NeoGrid.Row key={item.tradeOrderId}>
                                            <NeoGrid.Column
                                                label={
                                                    <span>
                                                        <input
                                                            type="checkbox"
                                                            checked={this.viewModel.selectAll}
                                                            onChange={e => this.viewModel.handleSelectAll(e.target.checked)}
                                                            style={{ marginRight: 6 }}
                                                        />
                                                        {TextConstants.Titles.SelectAll}
                                                    </span>
                                                }
                                            >
                                                {isPending && (
                                                    <input
                                                        type="checkbox"
                                                        checked={this.viewModel.selectedTradeOrderIDs.includes(String(item.tradeOrderId))}
                                                        onChange={e => this.viewModel.handleSelect(item, e.target.checked)}
                                                    />
                                                )}
                                            </NeoGrid.Column>
                                            <NeoGrid.Column label="Status" display={item.meta.tradeOrderStatusName} />
                                            <NeoGrid.Column label="Stockbroker Name" display={item.meta.staticServiceProviderFileGroupingName} />
                                            <NeoGrid.Column label="Trade Description" display={item.meta.tradeDescription} />
                                            <NeoGrid.Column label="Account Number" display={item.meta.accountNumber} />
                                            <NeoGrid.Column label="Fund Code" display={item.meta.fundCode} />
                                            <NeoGrid.Column label="Fund Name" display={item.meta.fundName} />
                                            <NeoGrid.Column label="Bulked Investor Code" display={item.meta.bulkedInvestorCode} />
                                            <NeoGrid.Column label="Bulked Investor Name" display={item.meta.bulkedInvestorName} />
                                            <NeoGrid.Column label="Product Code" display={item.meta.productCode} />
                                            <NeoGrid.Column label="Product Name" display={item.meta.productName} />
                                            <NeoGrid.Column label="Reference Number" display={item.meta.referenceNumber} />
                                            <NeoGrid.Column label="Percent" display={item.meta.percent} />
                                            <NeoGrid.Column label="Units" display={item.meta.units} />
                                            <NeoGrid.Column label="Currency" display={item.meta.currency} />
                                            <NeoGrid.Column label="Amount" display={item.meta.amount} />
                                            <NeoGrid.Column label="Confirmed Amount" display={item.meta.confirmedAmount ?? item.meta.amount ?? ""} />
                                            <NeoGrid.Column label="Cycle Date" display={item.meta.cycleDate} />
                                            <NeoGrid.Column label="Mode" display={item.meta.modeCode} />
                                            <NeoGrid.Column label="Fund Reference" display={item.meta.fundReference} />
                                            <NeoGrid.Column label="Message Type" display={item.meta.messageTypeName} />
                                            <NeoGrid.Column label="Service Provider Reference" display={item.meta.staticServiceProviderFileGroupingName} hidden />
                                            <NeoGrid.Column label="Note" display={item.meta.note} />
                                            <NeoGrid.ButtonColumn label="Actions">
                                                <div className="btn-group">
                                                    <Neo.Button
                                                        disabled={disableInlineActions || !isReleased}
                                                        variant="success"
                                                        size="sm"
                                                        isOutline
                                                        icon="fa fa-check"
                                                        title={TextConstants.Buttons.Confirm}
                                                        onClick={() => this.viewModel.showConfirm(item)}
                                                    />
                                                    <Neo.Button
                                                        disabled={disableInlineActions || !canReject}
                                                        variant="danger"
                                                        size="sm"
                                                        isOutline
                                                        icon="fa fa-times"
                                                        title={TextConstants.Buttons.Reject}
                                                        onClick={() => this.viewModel.showReject(item)}
                                                    />
                                                    <Neo.Button
                                                        disabled={disableInlineActions || !isRejected}
                                                        variant="warning"
                                                        size="sm"
                                                        isOutline
                                                        icon="fa fa-undo"
                                                        title={TextConstants.Buttons.ReInstate}
                                                        onClick={() => this.viewModel.showReinstate(item)}
                                                    />
                                                    {item.fileDescriptorId !== null && (
                                                        <Neo.Button
                                                            variant="primary"
                                                            size="sm"
                                                            isOutline
                                                            icon="fa fa-download"
                                                            title={TextConstants.Buttons.View}
                                                            onClick={() => this.viewModel.viewTradeOrderAttachment(item.tradeOrderId!)}
                                                        />
                                                    )}
                                                </div>
                                            </NeoGrid.ButtonColumn>
                                        </NeoGrid.Row>
                                    );
                                }}
                            </NeoGrid.Grid>
                        </div>
                    </Neo.Pager>
                </Neo.Card>

                {/* Confirm Modal */}
                <Neo.Modal
                    show={this.viewModel.showConfirmModal}
                    bindModel={this.viewModel.meta.confirmTradeOrder}
                    title={TextConstants.Titles.ConfirmTradeOrder}
                    onClose={this.viewModel.closeConfirm}
                    buttons={[
                        { text: TextConstants.Buttons.Save, variant: "success", icon: "fa fa-check", onClick: this.viewModel.submitConfirm },
                        { text: TextConstants.Buttons.Cancel, variant: "secondary", onClick: this.viewModel.closeConfirm }
                    ]}
                >
                    {(command: UpdateTradeOrderCommand) => (
                        <div>
                            {this.viewModel.confirmValidationMessage && (
                                <Neo.Alert variant="warning">
                                    <strong>{TextConstants.Titles.RequiredField}:</strong> {this.viewModel.confirmValidationMessage}
                                </Neo.Alert>
                            )}
                            {this.renderFileDropzone('confirm')}
                            <Neo.FormGroup
                                bind={command.meta.amount}
                                label={TextConstants.Labels.ExpectedAmount}
                                isReadOnly={true}
                            />
                            {this.viewModel.confirmIsFullDisinvestment && (
                                <Neo.Alert variant="info">
                                    TextConstants.Titles.FullDisinvestment
                                </Neo.Alert>
                            )}
                            <Neo.FormGroup
                                bind={command.meta.confirmedAmount}
                                label={TextConstants.Labels.ConfirmedAmount}
                                placeholder="Enter confirmed amount"
                                onChange={() => this.viewModel.clearConfirmValidationMessage()}
                            />
                            <Neo.FormGroup
                                bind={command.meta.note}
                                label={TextConstants.Labels.Notes}
                                placeholder="eg. Reason for confirmation"
                            />
                        </div>
                    )}
                </Neo.Modal>

                {/* Reject Modal */}
                <Neo.Modal
                    show={this.viewModel.showRejectModal}
                    bindModel={this.viewModel.meta.rejectTradeOrder}
                    title={TextConstants.Titles.RejectTradeOrder}
                    onClose={this.viewModel.closeReject}
                    buttons={[
                        { text: TextConstants.Buttons.Reject, variant: "danger", icon: "fa fa-times", onClick: this.viewModel.submitReject },
                        { text: TextConstants.Buttons.Cancel, variant: "secondary", onClick: this.viewModel.closeReject }
                    ]}
                >
                    {(command: UpdateTradeOrderCommand) => (
                        <div>
                            {this.renderFileDropzone('reject')}
                            <Neo.FormGroup
                                bind={command.meta.note}
                                label={TextConstants.Labels.RejectionReason}
                                placeholder="Please provide reason for rejection"
                            />
                        </div>
                    )}
                </Neo.Modal>

                {/* Reinstate Modal */}
                <Neo.Modal
                    show={this.viewModel.showReinstateModal}
                    bindModel={this.viewModel.meta.reinstateTradeOrder}
                    title={TextConstants.Titles.ReinstateTradeOrder}
                    onClose={this.viewModel.closeReinstate}
                    buttons={[
                        { text: "Yes, Re-Instate", variant: "warning", icon: "fa fa-undo", onClick: this.viewModel.submitReinstate },
                        { text: "No, Cancel", variant: "secondary", onClick: this.viewModel.closeReinstate }
                    ]}
                >
                    {(command: UpdateTradeOrderCommand) => (
                        <div>
                            <p>Are you sure you want to re-instate this trade order?</p>

                            <Neo.FormGroup
                                bind={command.meta.note}
                                label={TextConstants.Labels.Notes}
                                placeholder="Reason for reinstatement"
                            />
                        </div>
                    )}
                </Neo.Modal>
            </>
        );
    }
}